/**
 * Firm-level General Settings report note catalogue defaults.
 * Provides the default titles for all business types
 * (Limited Company, LLP, Partnership, Sole Trader) across Director's/Member's report,
 * Accountant's report, Auditor's report, and Accounting Policies.
 *
 * These titles are used as placeholder text in the settings inputs so the user can see
 * the system defaults before they have been customised, matching the AP3.0 behaviour.
 */

// ---- Limited Company / FRS102 / FRS105 Director's Report (11 points) ----

const REPORT_POINTS_TITLES: Record<number, string> = {
  1: "Director's report and financial statements",
  2: 'Principal Activities',
  3: "Director's responsibilities statement",
  4: 'Directors',
  5: 'Statement of disclosure of information to auditor',
  6: 'Auditors',
  7: 'Purchase of own shares',
  8: 'Share-based payment transactions',
  9: 'Political and charitable donations',
  10: 'Qualifying third party indemnity provisions',
  11: 'Employment of disabled persons',
};

// ---- LLP Member's Report (8 points) ----

const LLP_REPORT_POINTS_TITLES: Record<number, string> = {
  1: "Members' report and financial statements",
  2: 'Principal activities',
  3: 'Branches outside the UK',
  4: 'Designated Members',
  5: "Members' drawings and subscription and repayment of members' capital",
  6: "Transfer of members' interests",
  7: 'Political donations',
  8: 'Charitable donations',
};

// ---- Accountant's Report lines ----

// Must equal the API's default headings (FirmSettingsRules.CompanyAccountantReport): Reset Default restores them.
const ACCOUNTANT_REPORT_TITLES: Record<number, string> = {
  1: "Accountant's Report",
  2: 'Exceptions under Section 477',
  3: 'Directors Responsibilities 1',
  4: 'Directors Responsibilities 2',
  5: 'Special Provisions',
};

// ---- Partnership / Sole Trader Accountant's Report lines (2) — the API's unincorporated headings ----

const SMALL_ACCOUNTANT_REPORT_TITLES: Record<number, string> = {
  1: "Accountant's Information",
  2: 'Client Approval Certificate',
};

// ---- LLP Accountant's Report lines (4) — AP3.0 exact order ----

const LLP_ACCOUNTANT_REPORT_TITLES: Record<number, string> = {
  1: "Accountant's Report",
  2: 'Exceptions under Section 477',
  3: 'Members Responsibilities',
  4: 'Special Provisions',
};

// ---- Auditor's Report lines (Limited Company) ----

// Must equal the API's default headings (FirmSettingsRules.CompanyAuditorReport).
const AUDITOR_REPORT_TITLES: Record<number, string> = {
  1: 'Opinion',
  2: 'Responsibilities of directors',
  3: 'Basis for opinion',
  4: 'Opinion on other matters prescribed by the Companies Act 2006',
  5: 'Matters on which we are required to report by exception',
};

// ---- LLP Auditor's Report lines (6) — AP3.0 exact order ----

const LLP_AUDITOR_REPORT_TITLES: Record<number, string> = {
  1: "Auditor's information",
  2: 'Respective responsibilities of members and auditors',
  3: 'Scope of audit of the financial statements',
  4: 'Basis for qualified opinion on financial statements',
  5: 'Qualified opinion on financial statements',
  6: 'Matters on which we are required to report by exception',
};

// ---- Limited Company P&L Accounting Policies (18) ----
// Order matches the old AP3.0 app exactly (Significant accounting policies → Preference dividends).

const PL_POLICY_TITLES: Record<number, string> = {
  1: 'Significant accounting policies',
  2: 'Group accounts',
  3: 'Change in accounting policy',
  4: 'Going concern basis',
  5: 'Statement of cash flows',
  6: 'Turnover',
  7: 'Cost of sales',
  8: 'Supplier rebates',
  9: 'Government grants',
  10: 'Operating lease rentals',
  11: 'Finance lease and hire purchase charges',
  12: 'Research and development expenditure',
  13: 'Website cost',
  14: 'Foreign currencies',
  15: 'Taxation',
  16: 'Deferred taxation',
  17: 'Dividends',
  18: 'Preference dividends',
};

// ---- Limited Company Balance Sheet Accounting Policies (15) ----
// Order matches the old AP3.0 app exactly (Intangible assets → Financial Commitments…).
// bsPolicy2-5 (Goodwill, Licences and patents, Development expenditures,
// Computer software development costs) are rendered indented under bsPolicy1 (Intangible assets)
// — see LimitedCompanyScreen LAYOUT indented list.

const BS_POLICY_TITLES: Record<number, string> = {
  1: 'Intangible assets',
  2: 'Goodwill',
  3: 'Licences and patents',
  4: 'Development expenditures',
  5: 'Computer software development costs',
  6: 'Tangible fixed assets',
  7: 'Investment properties',
  8: 'Assets on finance lease and hire purchase',
  9: 'Fixed asset investments',
  10: 'Stocks',
  11: 'Factored debts',
  12: 'Current asset investments',
  13: 'Provisions',
  14: 'Preference shares',
  15: 'Financial Commitments, Guarantees and Contingencies',
};

// ---- Partnership / Sole Trader P&L Accounting Policies (12) ----
// Group accounts, change in accounting policy, taxation, deferred taxation, dividends and preference dividends are
// left out: the proprietor/partners are taxed on the profit and take drawings, not dividends, and there are no
// subsidiaries to consolidate or shares to account for.

const SMALL_PL_POLICY_TITLES: Record<number, string> = {
  1: 'Significant accounting policies',
  2: 'Going concern basis',
  3: 'Statement of cash flows',
  4: 'Turnover',
  5: 'Cost of sales',
  6: 'Supplier rebates',
  7: 'Government grants',
  8: 'Operating lease rentals',
  9: 'Finance lease and hire purchase charges',
  10: 'Research and development expenditure',
  11: 'Website cost',
  12: 'Foreign currencies',
};

// ---- Partnership / Sole Trader Balance Sheet Accounting Policies (14) — AP3.0 exact order ----
// bsPolicy2-5 (Goodwill … Computer software development costs) are indented under bsPolicy1 (Intangible assets).

const SMALL_BS_POLICY_TITLES: Record<number, string> = {
  1: 'Intangible assets',
  2: 'Goodwill',
  3: 'Licences and patents',
  4: 'Development expenditures',
  5: 'Computer software development costs',
  6: 'Tangible fixed assets',
  7: 'Investment properties',
  8: 'Assets on finance lease and hire purchase',
  9: 'Fixed asset investments',
  10: 'Stocks',
  11: 'Factored debts',
  12: 'Current asset investments',
  13: 'Provisions',
  14: 'Financial Commitments, Guarantees and Contingencies',
};

// ---- LLP Accounting Policies (18 P&L + 15 BS) — AP3.0 exact order ----
// Identical to Limited Company per the old application.

const LLP_PL_POLICY_TITLES: Record<number, string> = {
  1: 'Significant accounting policies',
  2: 'Group accounts',
  3: 'Change in accounting policy',
  4: 'Going concern basis',
  5: 'Statement of cash flows',
  6: 'Turnover',
  7: 'Cost of sales',
  8: 'Supplier rebates',
  9: 'Government grants',
  10: 'Operating lease rentals',
  11: 'Finance lease and hire purchase charges',
  12: 'Research and development expenditure',
  13: 'Website cost',
  14: 'Foreign currencies',
  15: 'Taxation',
  16: 'Deferred taxation',
  17: 'Dividends',
  18: 'Preference dividends',
};

// bsPolicy2-5 (Goodwill … Computer software development costs) are indented under bsPolicy1 (Intangible assets).
const LLP_BS_POLICY_TITLES: Record<number, string> = {
  1: 'Intangible assets',
  2: 'Goodwill',
  3: 'Licences and patents',
  4: 'Development expenditures',
  5: 'Computer software development costs',
  6: 'Tangible fixed assets',
  7: 'Investment properties',
  8: 'Assets on finance lease and hire purchase',
  9: 'Fixed asset investments',
  10: 'Stocks',
  11: 'Factored debts',
  12: 'Current asset investments',
  13: 'Provisions',
  14: 'Preference shares',
  15: 'Financial Commitments, Guarantees and Contingencies',
};

function isSmallBusiness(settingsKey?: string): boolean {
  return settingsKey === 'partnership' || settingsKey === 'sole-trader';
}

/**
 * Returns the default/placeholder policy title for a given control name in General Settings.
 * `settingsKey` is used to distinguish LLP member's report from limited-company director's report.
 */
export function getNoteTitle(name: string, settingsKey?: string): string {
  const match = name.match(/^([a-zA-Z]+)(\d+)$/);
  if (!match) return '';
  const prefix = match[1];
  const index = parseInt(match[2]!, 10);

  if (prefix === 'reportPoint') {
    if (settingsKey === 'llp') {
      return LLP_REPORT_POINTS_TITLES[index] ?? '';
    }
    return REPORT_POINTS_TITLES[index] ?? '';
  }

  if (prefix === 'accountantReport') {
    if (settingsKey === 'llp') return LLP_ACCOUNTANT_REPORT_TITLES[index] ?? '';
    if (isSmallBusiness(settingsKey)) return SMALL_ACCOUNTANT_REPORT_TITLES[index] ?? '';
    return ACCOUNTANT_REPORT_TITLES[index] ?? '';
  }

  if (prefix === 'auditorReport') {
    if (settingsKey === 'llp') return LLP_AUDITOR_REPORT_TITLES[index] ?? '';
    return AUDITOR_REPORT_TITLES[index] ?? '';
  }

  if (prefix === 'plPolicy') {
    if (settingsKey === 'llp') return LLP_PL_POLICY_TITLES[index] ?? '';
    if (settingsKey === 'partnership' || settingsKey === 'sole-trader') return SMALL_PL_POLICY_TITLES[index] ?? '';
    // every limited taxonomy shares the old app's one 18-entry list (UK-GAAP, FRS102, FRS102 Section 1A, FRS105)
    return PL_POLICY_TITLES[index] ?? '';
  }

  if (prefix === 'bsPolicy') {
    if (settingsKey === 'llp') return LLP_BS_POLICY_TITLES[index] ?? '';
    if (settingsKey === 'partnership' || settingsKey === 'sole-trader') return SMALL_BS_POLICY_TITLES[index] ?? '';
    // every limited taxonomy shares the old app's one 15-entry list
    return BS_POLICY_TITLES[index] ?? '';
  }

  return '';
}

export interface NoteDefault {
  title: string;
  content: string;
}

const REPORT_POINTS_CONTENTS: Record<number, string> = {
  1: '[director_statement] his/her/their annual report and the audited financial statements for the year ended [date_period_end]',
  2: 'The principal activity of the company during the year was that of general commercial activities.',
  3: "The directors are responsible for preparing the Director's Report and the financial statements in accordance with applicable law and regulations.",
  4: 'The directors who held office during the [period] and up to the date of signature of the financial statements were as follows:\n[display_directors]',
  5: "So far as each person who was a director at the date of approving this report is aware, there is no relevant audit information of which the company's auditors are unaware.",
  6: 'The auditors, [accountant_name], will be proposed for re-appointment at the forthcoming Annual General Meeting.',
  7: 'The company has not acquired any of its own shares during the [period].',
  8: 'The company did not enter into any share-based payment transactions during the [period].',
  9: 'During the [period], the company made no political or charitable donations.',
  10: 'No qualifying third party indemnity provisions were in force for the benefit of one or more directors during the [period].',
  11: 'The company has a policy of giving full and fair consideration to applications for employment from disabled persons.',
};

// The 3.0 LLP "Member's Report Points" wording (DefaultNotes ids 120-125); kept in step with FirmNoteControls on the API, which is
// the source the popup reads - these are only shown while that has not loaded.
const LLP_REPORT_POINTS_CONTENTS: Record<number, string> = {
  1: 'The members present their financial statements for the [period] ended [date_period_end].',
  2: 'The principal activity during the [period] continued to be',
  3: 'The LLP has branches outside the United Kingdom.',
  4: 'The designated members during the [period] were as follows:\n[display_directors]',
  5: 'Members are permitted to make drawings in anticipation of profits which will be allocated to them. The amount of such drawings is set at the beginning of each financial year, taking into account the anticipated cash needs of the LLP. New members are required to subscribe a minimum level of capital and in subsequent years members are invited to subscribe for further capital, the amount of which is determined by the performance and seniority of those members. On retirement, capital is repaid to members.',
  6: "During the [period] £x was transferred from/to members' equity interests to/from members' debt interests. Since the [period] end further amounts totaling £x have been transferred.",
  7: 'During the [period] the LLP made the following political donations:',
  8: 'During the [period] the LLP made the following charitable donations:',
};

const ACCOUNTANT_REPORT_CONTENTS: Record<number, string> = {
  1: 'In order to assist you to fulfil your duties under the Companies Act 2006, we have prepared for your approval the accounts of [Company name] for the year ended [date_period_end] from the accounting records and information supplied to us.',
  2: 'For the [accounting_period] the company was entitled to exemption from audit under section 477 of the Companies Act 2006 relating to small companies.',
  3: 'The directors are responsible for the preparation of the financial statements and for being satisfied that they give a true and fair view.',
  4: 'The directors acknowledge their responsibilities for complying with the requirements of the Companies Act 2006 with respect to accounting records and the preparation of accounts.',
  5: 'These accounts have been prepared and delivered in accordance with the provisions applicable to companies subject to the small companies regime.',
};

// Partnership and sole trader: proprietor / partners wording, no Companies Act (db/seed/36_unincorporated_notes.sql).
const SMALL_ACCOUNTANT_REPORT_CONTENTS: Record<number, string> = {
  1: 'In accordance with the terms of our engagement, we have prepared for your approval the accounts of [Company name] for the [accounting_period] which comprise the profit and loss account, the balance sheet and the related notes, from the accounting records of the business and from information and explanations you have given us.',
  2: 'I approve these accounts and I confirm that all relevant records and information have been made available in their preparation.',
};

const LLP_ACCOUNTANT_REPORT_CONTENTS: Record<number, string> = {
  1: 'In accordance with our engagement letter, we have compiled the financial statements of [Company name] for the year ended [date_period_end] from the accounting records and information supplied to us.',
  2: 'For the [period] ending [date_period_end] the LLP was entitled to exemption under section 477 of the Companies Act 2006 (as applied to LLPs) relating to small LLPs.',
  3: 'The members acknowledge their responsibilities for complying with the requirements of the Companies Act 2006 (as applied to LLPs) with respect to accounting records and the preparation of accounts.',
  4: 'These financial statements have been prepared in accordance with the special provisions relating to LLPs subject to the small LLPs regime within Part 15 of the Companies Act 2006.',
};

const LLP_AUDITOR_REPORT_CONTENTS: Record<number, string> = {
  1: 'We have audited the financial statements of [Company name] for the year ended [date_period_end] which comprise the [selected_report_pages] and the related notes.',
  2: "As explained more fully in the Members' Responsibilities Statement the members are responsible for the preparation of the financial statements and for being satisfied that they give a true and fair view. Our responsibility is to audit and express an opinion on the financial statements in accordance with applicable law and International Standards on Auditing (UK and Ireland). Those standards require us to comply with the Auditing Practices Board's (APB's) Ethical Standards for Auditors.",
  3: "A description of the scope of an audit of financial statements is provided on the APB's website at http://www.frc.org.uk/apb/scope/private.cfm",
  4: 'The basis for our qualified opinion is set out below.',
  5: "In our opinion, except for the possible effects of the matter described in the Basis for Qualified Opinion paragraph, the financial statements give a true and fair view of the state of the LLP's affairs as at [date_period_end].",
  6: 'We have nothing to report in respect of the following matters where the Companies Act 2006 requires us to report to you if, in our opinion: 1) adequate accounting records have not been kept, or returns adequate for our audit have not been received from the branches not visited by us; or 2) the financial statements are not in agreement with the accounting records and returns; or 3) we have not received all the information and explanations we require for our audit; 4) the members were not entitled to prepare the financial statements in accordance with the small limited liability partnerships regime.',
};

const AUDITOR_REPORT_CONTENTS: Record<number, string> = {
  1: 'We have audited the financial statements of [Company name] for the year ended [date_period_end] which comprise the [selected_report_pages] and the related notes.',
  2: "As explained more fully in the Directors' Responsibilities Statement, the directors are responsible for the preparation of the financial statements and for being satisfied that they give a true and fair view.",
  3: 'We conducted our audit in accordance with International Standards on Auditing (UK) (ISAs (UK)) and applicable law.',
  4: "In our opinion, based on the work undertaken in the course of the audit, the information given in the directors' report for the financial year is consistent with the financial statements.",
  5: "In the light of the knowledge and understanding of the company and its environment obtained in the course of the audit, we have not identified material misstatements in the directors' report.",
};

const PL_POLICY_CONTENTS: Record<number, string> = {
  1: 'The financial statements have been prepared under the historical cost convention and in accordance with applicable accounting standards.',
  2: 'The company has taken advantage of the exemption under section 400 of the Companies Act 2006 not to prepare consolidated financial statements.',
  3: 'The accounting policies have been applied consistently throughout the period.',
  4: 'The financial statements have been prepared on a going concern basis.',
  5: 'The company is exempt from preparing a cash flow statement.',
  6: 'Turnover is measured at the fair value of the consideration received or receivable, net of discounts and value added taxes.',
  7: 'Cost of sales includes direct costs and expenses incurred in delivering goods or services.',
  8: 'Supplier rebates are recognized when the right to receive payment has been established.',
  9: 'Government grants are recognized when there is reasonable assurance that the entity will comply with the conditions.',
  10: 'Rentals payable under operating leases are charged to profit or loss on a straight-line basis over the lease term.',
  11: 'Finance lease and hire purchase charges are allocated to periods over the lease term.',
  12: 'Research and development expenditure is written off in the year in which it is incurred.',
  13: 'Website development costs are capitalized to the extent they lead to future economic benefits.',
  14: 'Transactions in foreign currencies are recorded at the rate ruling at the date of the transaction.',
  15: 'The tax expense for the period comprises current and deferred tax.',
  16: 'Deferred tax is recognized in respect of all timing differences that have originated but not reversed at the balance sheet date.',
  17: 'Dividends are recognized when they become legally payable.',
  18: 'Preference dividends are recognized when declared and approved.',
};

const BS_POLICY_CONTENTS: Record<number, string> = {
  1: 'Intangible assets are initially measured at cost and amortised over their estimated useful lives.',
  2: 'Goodwill is amortised over its expected useful life.',
  3: 'Licences and patents are amortised over their estimated useful lives.',
  4: 'Development expenditure is capitalised only when specific criteria are met.',
  5: 'Computer software development costs are amortised over their expected useful life.',
  6: 'Tangible fixed assets are measured at cost less accumulated depreciation and any accumulated impairment losses.',
  7: 'Investment properties are measured initially at cost and subsequently at fair value.',
  8: 'Assets on finance lease and hire purchase are capitalized and depreciated over their useful lives.',
  9: 'Fixed asset investments are stated at cost less provision for impairment.',
  10: 'Stocks are stated at the lower of cost and net realisable value.',
  11: 'Factored debts are accounted for in accordance with the substance of the transaction.',
  12: 'Current asset investments are valued at the lower of cost and net realisable value.',
  13: 'Provisions are recognised when the company has an obligation at the reporting date as a result of a past event.',
  14: 'Preference shares are classified as equity or debt based on the terms of the issue.',
  15: 'Contingent liabilities are disclosed unless the possibility of an outflow of resources is remote.',
};

/**
 * A policy's wording is found by its heading: the FRS 105, Section 1A, LLP, partnership and sole trader lists leave policies
 * out, so a policy's position is not the position of its wording in the limited company's list.
 */
function policyContent(titles: Record<number, string>, contents: Record<number, string>, title: string): string {
  const index = Object.entries(titles).find(([, heading]) => heading === title)?.[0];
  return index === undefined ? '' : (contents[Number(index)] ?? '');
}

/**
 * BUG-001: the catalogue's policy wording is written for a limited company; an LLP, partnership or sole trader reads it as
 * members / the LLP, partners / the partnership or the proprietor / the business, as the API's default wording does.
 */
function wordingFor(text: string, settingsKey?: string): string {
  const swap = (pairs: [string, string][]) => pairs.reduce((out, [from, to]) => out.split(from).join(to), text);
  if (settingsKey === 'llp') {
    return swap([
      ['the company', 'the LLP'],
      ['The company', 'The LLP'],
    ]);
  }
  if (settingsKey === 'partnership') {
    return swap([
      ['the company', 'the partnership'],
      ['The company', 'The partnership'],
      ['directors', 'partners'],
    ]);
  }
  if (settingsKey === 'sole-trader') {
    return swap([
      ['the company', 'the business'],
      ['The company', 'The business'],
      ['directors', 'proprietor'],
    ]);
  }
  return text;
}

/**
 * Returns default title and content for a given control name in General Settings.
 */
export function getNoteDefault(name: string, settingsKey?: string): NoteDefault {
  const title = getNoteTitle(name, settingsKey);
  const match = name.match(/^([a-zA-Z]+)(\d+)$/);
  if (!match) {
    return { title: title || name, content: '' };
  }
  const prefix = match[1];
  const index = parseInt(match[2]!, 10);

  if (prefix === 'reportPoint') {
    const content =
      settingsKey === 'llp' ? (LLP_REPORT_POINTS_CONTENTS[index] ?? '') : (REPORT_POINTS_CONTENTS[index] ?? '');
    return { title, content };
  }

  if (prefix === 'accountantReport') {
    const content =
      settingsKey === 'llp'
        ? (LLP_ACCOUNTANT_REPORT_CONTENTS[index] ?? '')
        : isSmallBusiness(settingsKey)
          ? (SMALL_ACCOUNTANT_REPORT_CONTENTS[index] ?? '')
          : (ACCOUNTANT_REPORT_CONTENTS[index] ?? '');
    return { title, content };
  }

  if (prefix === 'auditorReport') {
    const content =
      settingsKey === 'llp' ? (LLP_AUDITOR_REPORT_CONTENTS[index] ?? '') : (AUDITOR_REPORT_CONTENTS[index] ?? '');
    return { title, content };
  }

  if (prefix === 'plPolicy') {
    return { title, content: wordingFor(policyContent(PL_POLICY_TITLES, PL_POLICY_CONTENTS, title), settingsKey) };
  }

  if (prefix === 'bsPolicy') {
    return { title, content: wordingFor(policyContent(BS_POLICY_TITLES, BS_POLICY_CONTENTS, title), settingsKey) };
  }

  return { title, content: '' };
}
